import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends, typeof as _typeof } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useMemo, useCallback, useEffect, useImperativeHandle } from 'react';
import useSize from '../../../hooks/use-size.esm.js';
import useTimeout from '../../../hooks/use-timeout.esm.js';
import { isFlatDesign } from '../../../themes/const.esm.js';
import { isNotEmptyObject, __DEV__ } from '../../../utils/assertion.esm.js';
import debounce from '../../../utils/debounce.esm.js';
import getScrollbarSize from '../../../utils/get-scrollbar-size.esm.js';
import { EMPTY_OBJECT } from '../../../utils/object.esm.js';
import ownerDocument from '../../../utils/owner-document.esm.js';
import ownerWindow from '../../../utils/owner-window.esm.js';
import { PanePrivate } from '../../_layouts/pane/pane.esm.js';
import { ClickAwayListener } from '../click-away-listener/click-away-listener.esm.js';
import Grow from '../grow/grow.esm.js';
import Modal from '../modal/modal.esm.js';
import { useSafeThemeUI } from '../../../theme-provider/use-theme-ui.esm.js';

var _excluded = ["action", "anchorEl", "anchorOrigin", "anchorPosition", "anchorReference", "children", "container", "getContentAnchorEl", "marginThreshold", "onEnter", "onEntered", "onEntering", "onExit", "onExited", "onExiting", "onReposition", "open", "paneProps", "transformOrigin", "TransitionComponent", "transitionDuration", "TransitionProps", "transitionStyles", "positionOffset", "doNotCoverAnchorEl", "useContentHeight", "disableVerticalAdjustment", "clickAwayListenerProps", "refreshToken"],
  _excluded2 = ["onEnter", "onEntering", "onEntered", "onExit", "onExited", "onExiting"],
  _excluded3 = ["elevation", "border", "__css"];
// import useWhyDidYouUpdate from '../../../hooks/use-why-did-you-update'

function getOffsetTop(rect, vertical) {
  var offset = 0;
  if (typeof vertical === 'number') {
    offset = vertical;
  } else if (_typeof(rect) === 'object') {
    if (vertical === 'center') {
      offset = rect.height / 2;
    } else if (vertical === 'bottom') {
      offset = rect.height;
    }
  }
  return offset;
}
function getOffsetLeft(rect, horizontal) {
  var offset = 0;
  if (typeof horizontal === 'number') {
    offset = horizontal;
  } else if (_typeof(rect) === 'object') {
    if (horizontal === 'center') {
      offset = rect.width / 2;
    } else if (horizontal === 'right') {
      offset = rect.width;
    }
  }
  return offset;
}
var getPopoverAndAnchorStatus = function getPopoverAndAnchorStatus(popoverElement, anchorElement) {
  var ol;
  var it;
  var ib;
  if (popoverElement && anchorElement) {
    var _popoverElement$getBo, _ref, _anchorElement$getBou, _ref2;
    var pt = Math.round((_popoverElement$getBo = popoverElement === null || popoverElement === void 0 ? void 0 : popoverElement.getBoundingClientRect().top) !== null && _popoverElement$getBo !== void 0 ? _popoverElement$getBo : 0);
    var pb = Math.round((_ref = (popoverElement === null || popoverElement === void 0 ? void 0 : popoverElement.getBoundingClientRect().top) + (popoverElement === null || popoverElement === void 0 ? void 0 : popoverElement.offsetHeight)) !== null && _ref !== void 0 ? _ref : 0);
    var at = Math.round((_anchorElement$getBou = anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.getBoundingClientRect().top) !== null && _anchorElement$getBou !== void 0 ? _anchorElement$getBou : 0);
    var ab = Math.round((_ref2 = (anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.getBoundingClientRect().top) + (anchorElement === null || anchorElement === void 0 ? void 0 : anchorElement.offsetHeight)) !== null && _ref2 !== void 0 ? _ref2 : 0);
    ol = pt < at + 1 && pb > at + 1 || pt < ab - 1 && pb > ab - 1;

    // the +/- 1 is because even though the top and bottom pixels of each element line
    // up in the UI, for some reason, the popover is one pixel off.
    it = at + 1 - pb === 0;
    ib = ab - 1 - pt === 0;
  } else {
    ol = undefined;
    it = undefined;
    ib = undefined;
  }
  return {
    isPopoverOverlappingAnchor: ol,
    isPopoverOpeningUp: it,
    isPopoverOpeningDown: ib
  };
};
function getTransformOriginValue(transformOrigin) {
  return [transformOrigin.horizontal, transformOrigin.vertical].map(function (n) {
    return typeof n === 'number' ? "".concat(n, "px") : n;
  }).join(' ');
}

// Sum the scrollTop between two elements.
function getScrollParent(parent, child) {
  var element = child;
  var scrollTop = 0;
  while (element && element !== parent) {
    element = element.parentElement;
    scrollTop += element.scrollTop;
  }
  return scrollTop;
}
function getAnchorEl(anchorEl) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
}
var defaultOffset = {
  x: 0,
  y: 0
};
var backdropProps = {
  invisible: true
};
var Popover = /*#__PURE__*/React.forwardRef(function Popover(props, ref) {
  // eslint-disable-next-line no-console
  // console.log(` ---- popover ----`)
  // useWhyDidYouUpdate('Popover', props)
  var action = props.action,
    anchorEl = props.anchorEl,
    _props$anchorOrigin = props.anchorOrigin,
    anchorOrigin = _props$anchorOrigin === void 0 ? {
      vertical: 'top',
      horizontal: 'left'
    } : _props$anchorOrigin,
    anchorPosition = props.anchorPosition,
    _props$anchorReferenc = props.anchorReference,
    anchorReference = _props$anchorReferenc === void 0 ? 'anchorEl' : _props$anchorReferenc,
    children = props.children,
    containerProp = props.container,
    getContentAnchorEl = props.getContentAnchorEl,
    _props$marginThreshol = props.marginThreshold,
    marginThreshold = _props$marginThreshol === void 0 ? 12 : _props$marginThreshol,
    onEnter = props.onEnter,
    onEntered = props.onEntered,
    onEntering = props.onEntering,
    onExit = props.onExit,
    onExited = props.onExited,
    onExiting = props.onExiting,
    onReposition = props.onReposition,
    open = props.open,
    _props$paneProps = props.paneProps,
    paneProps = _props$paneProps === void 0 ? EMPTY_OBJECT : _props$paneProps,
    _props$transformOrigi = props.transformOrigin,
    transformOrigin = _props$transformOrigi === void 0 ? {
      vertical: 'top',
      horizontal: 'left'
    } : _props$transformOrigi,
    _props$TransitionComp = props.TransitionComponent,
    TransitionComponent = _props$TransitionComp === void 0 ? Grow : _props$TransitionComp,
    _props$transitionDura = props.transitionDuration,
    transitionDurationProp = _props$transitionDura === void 0 ? 'auto' : _props$transitionDura,
    _props$TransitionProp = props.TransitionProps,
    TransitionProps = _props$TransitionProp === void 0 ? EMPTY_OBJECT : _props$TransitionProp,
    _props$transitionStyl = props.transitionStyles,
    transitionStyles = _props$transitionStyl === void 0 ? EMPTY_OBJECT : _props$transitionStyl,
    _props$positionOffset = props.positionOffset,
    positionOffsetProp = _props$positionOffset === void 0 ? EMPTY_OBJECT : _props$positionOffset,
    doNotCoverAnchorEl = props.doNotCoverAnchorEl,
    useContentHeight = props.useContentHeight,
    disableVerticalAdjustment = props.disableVerticalAdjustment,
    _props$clickAwayListe = props.clickAwayListenerProps,
    clickAwayListenerProps = _props$clickAwayListe === void 0 ? EMPTY_OBJECT : _props$clickAwayListe,
    refreshToken = props.refreshToken,
    other = _objectWithoutProperties(props, _excluded);
  var theme = useSafeThemeUI(props);
  var isFlatDesign$1 = isFlatDesign(theme);
  var paneRef = useRef(null);
  var ignoreResizeEventRef = useRef(false);
  var paneSize = useSize(paneRef === null || paneRef === void 0 ? void 0 : paneRef.current);
  var positionOffset = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, defaultOffset), positionOffsetProp);
  }, [positionOffsetProp]);
  var getAnchorRect = useCallback(function () {
    var resolvedAnchorEl = getAnchorEl(anchorEl);

    // If an anchor element wasn't provided, just use the parent body element of this Popover
    var anchorElement = resolvedAnchorEl && resolvedAnchorEl.nodeType === 1 ? resolvedAnchorEl : ownerDocument(paneRef.current).body;
    return anchorElement.getBoundingClientRect();
  }, [anchorEl]);

  // Returns the top/left offset of the position
  // to attach to on the anchor element (or body if none is provided)
  var getAnchorOffset = useCallback(function (contentAnchorOffset) {
    if (anchorReference === 'anchorPosition') {
      if (process.env.NODE_ENV !== 'production') {
        if (!anchorPosition) {
          console.error('Tui: you need to provide a `anchorPosition` prop when using ' + '<Popover anchorReference="anchorPosition" />.');
        }
      }
      return anchorPosition;
    }
    var anchorRect = getAnchorRect();
    if (process.env.NODE_ENV !== 'production') {
      var box = anchorRect;
      if (process.env.NODE_ENV !== 'test' && box.top === 0 && box.left === 0 && box.right === 0 && box.bottom === 0) {
        // eslint-disable-next-line no-console
        console.warn(['Tui: the `anchorEl` prop provided to the component is invalid.', 'The anchor element should be part of the document layout.', "Make sure the element is present in the document or that it's not display none."].join('\n'));
      }
    }
    var anchorVertical = contentAnchorOffset === 0 ? anchorOrigin.vertical : 'center';
    return {
      top: anchorRect.top + getOffsetTop(anchorRect, anchorVertical) + (isFlatDesign$1 ? 4 : 0),
      left: anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal)
    };
  }, [anchorOrigin.horizontal, anchorOrigin.vertical, anchorPosition, anchorReference, getAnchorRect, isFlatDesign$1]);

  // Returns the vertical offset of inner content to anchor the transform on if provided
  var getContentAnchorOffset = useCallback(function (element) {
    var contentAnchorOffset = 0;
    if (getContentAnchorEl && anchorReference === 'anchorEl') {
      var contentAnchorEl = getContentAnchorEl(element);
      if (contentAnchorEl && element.contains(contentAnchorEl)) {
        var scrollTop = getScrollParent(element, contentAnchorEl);
        contentAnchorOffset = contentAnchorEl.offsetTop + contentAnchorEl.clientHeight / 2 - scrollTop || 0;
      }

      // != the default value
      if (process.env.NODE_ENV !== 'production') {
        if (anchorOrigin.vertical !== 'top') {
          console.error(['Tui: you can not change the default `anchorOrigin.vertical` value ', 'when also providing the `getContentAnchorEl` prop to the popover component.', 'Only use one of the two props.', 'Set `getContentAnchorEl` to `null | undefined`' + ' or leave `anchorOrigin.vertical` unchanged.'].join('\n'));
        }
      }
    }
    return contentAnchorOffset;
  }, [anchorOrigin.vertical, anchorReference, getContentAnchorEl]);

  // Return the base transform origin using the element
  // and taking the content anchor offset into account if in use
  var getTransformOrigin = useCallback(function (elemRect) {
    var contentAnchorOffset = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    return {
      vertical: getOffsetTop(elemRect, transformOrigin.vertical) + contentAnchorOffset,
      horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal)
    };
  }, [transformOrigin.horizontal, transformOrigin.vertical]);
  var getPositioningStyle = useCallback(function (element) {
    var _anchorOffset$top, _window$getComputedSt, _window$getComputedSt2, _window$getComputedSt3;
    // Check if the parent has requested anchoring on an inner content node
    var contentAnchorOffset = getContentAnchorOffset(element);
    //reset the bottom of the element before computing it below
    element.style.bottom = '';
    var elemRect = {
      width: element.offsetWidth,
      height: element.offsetHeight
    };

    // Get the transform origin point on the element itself
    var elemTransformOrigin = getTransformOrigin(elemRect, contentAnchorOffset);
    if (anchorReference === 'none') {
      return {
        positioning: {
          top: null,
          left: null,
          bottom: null,
          transformOrigin: getTransformOriginValue(elemTransformOrigin)
        }
      };
    }

    // Get the offset of the anchoring element
    var anchorOffset = getAnchorOffset(contentAnchorOffset);

    // Calculate element positioning
    var top = (anchorOffset ? anchorOffset.top : 0) - elemTransformOrigin.vertical;
    var left = (anchorOffset ? anchorOffset.left : 0) - elemTransformOrigin.horizontal;
    var bottom = top + elemRect.height;
    var right = left + elemRect.width;
    // eslint-disable-next-line no-console
    // console.log(
    //   ` ---- popover initial position: ${JSON.stringify(
    //     {
    //       top,
    //       left,
    //       bottom,
    //       right
    //     },
    //     undefined,
    //     2
    //   )}`
    // )

    // Use the parent window of the anchorEl if provided
    var containerWindow = ownerWindow(getAnchorEl(anchorEl));

    // Window thresholds taking required margin into account
    var heightThreshold = containerWindow.innerHeight - marginThreshold;
    var widthThreshold = containerWindow.innerWidth - marginThreshold;

    // Check if the vertical axis needs shifting
    if (!disableVerticalAdjustment) {
      if (top < marginThreshold) {
        var diff = top - marginThreshold;
        top -= diff;
      } else if (bottom > heightThreshold) {
        var _diff = bottom - heightThreshold;
        top -= _diff;
      }
      // check top margin again in the case we shifted too close to the top
      if (top < marginThreshold) {
        var _diff2 = top - marginThreshold;
        top -= _diff2;
      }
    }

    // eslint-disable-next-line no-console
    // console.log(
    //   ` ---- popover after vertical shift position: ${JSON.stringify(
    //     {
    //       top,
    //       left,
    //       bottom,
    //       right
    //     },
    //     undefined,
    //     2
    //   )}`
    // )
    var anchorRect = getAnchorRect();
    if (doNotCoverAnchorEl && top < ((_anchorOffset$top = anchorOffset === null || anchorOffset === void 0 ? void 0 : anchorOffset.top) !== null && _anchorOffset$top !== void 0 ? _anchorOffset$top : 0)) {
      var _anchorOffset$top2;
      top = ((_anchorOffset$top2 = anchorOffset === null || anchorOffset === void 0 ? void 0 : anchorOffset.top) !== null && _anchorOffset$top2 !== void 0 ? _anchorOffset$top2 : 0) - elemRect.height - anchorRect.height + 1;

      // eslint-disable-next-line no-console
      // console.log(` ---- popover doNotCoverAnchorEl adjustment top: ${top}`)
    }
    if (process.env.NODE_ENV !== 'production') {
      if (elemRect.height > heightThreshold && elemRect.height && heightThreshold) {
        console.error(['Tui: the popover component is too tall.', "Some part of it can not be seen on the screen (".concat(elemRect.height - heightThreshold, "px)."), 'Please consider adding a `max-height` to improve the user-experience.'].join('\n'));
      }
    }

    //if there is a scrollbar gutter on the left, be sure to account for that and shift popover
    if ((_window$getComputedSt = (_window$getComputedSt2 = window.getComputedStyle(document.documentElement)) === null || _window$getComputedSt2 === void 0 ? void 0 : (_window$getComputedSt3 = _window$getComputedSt2.scrollbarGutter) === null || _window$getComputedSt3 === void 0 ? void 0 : _window$getComputedSt3.includes('both-edges')) !== null && _window$getComputedSt !== void 0 ? _window$getComputedSt : false) {
      var scrollbarWidth = getScrollbarSize();
      left -= scrollbarWidth;
    }

    // Check if the horizontal axis needs shifting
    if (left < marginThreshold) {
      var _diff3 = left - marginThreshold;
      left -= _diff3;
    } else if (right > widthThreshold) {
      var _diff4 = right - widthThreshold;
      left -= _diff4;
    }
    top = Math.round(top + positionOffset.y);
    left = Math.round(left + positionOffset.x);
    var rect = {
      top: top < marginThreshold ? marginThreshold : top,
      left: left < marginThreshold ? marginThreshold : left,
      bottom: !useContentHeight && !doNotCoverAnchorEl && bottom >= heightThreshold ? marginThreshold : elemRect.height + top
    };
    var retVal = {
      top: "".concat(rect.top, "px"),
      left: "".concat(rect.left, "px"),
      bottom: !useContentHeight && !doNotCoverAnchorEl && bottom >= heightThreshold ? "".concat(rect.bottom, "px") : null,
      transformOrigin: getTransformOriginValue({
        vertical: (rect.top + rect.bottom) / 2 < (anchorRect.top + anchorRect.bottom) / 2 ? "".concat(anchorRect.top - rect.top, "px") : "0px",
        horizontal: "".concat(left - Math.round(anchorRect.left), "px")
      })
    };

    // eslint-disable-next-line no-console
    // console.log(
    //   ` ---- popover getPositioningStyle retVal: ${JSON.stringify(
    //     retVal,
    //     undefined,
    //     2
    //   )}`
    // )

    return {
      positioning: retVal,
      heightThreshold: heightThreshold,
      widthThreshold: widthThreshold
    };
  }, [anchorEl, anchorReference, getAnchorOffset, getContentAnchorOffset, getTransformOrigin, getAnchorRect, marginThreshold, positionOffset, doNotCoverAnchorEl, useContentHeight, disableVerticalAdjustment]);
  var delayExecution = useTimeout(100);
  var setPositioningStyles = useCallback(function (refreshToken) {
    var element = paneRef.current;
    if (!element) {
      return;
    }
    var _getPositioningStyle = getPositioningStyle(element),
      positioning = _getPositioningStyle.positioning,
      heightThreshold = _getPositioningStyle.heightThreshold;
    // eslint-disable-next-line no-console
    // console.log(
    //   `--- popover setPositioningStyles() positioning: ${JSON.stringify(
    //     positioning,
    //     undefined,
    //     2
    //   )}`
    // )

    if (positioning.top !== null && element.style.top !== positioning.top || positioning.left !== null && element.style.left !== positioning.left || positioning.bottom !== null && positioning.bottom !== "".concat(marginThreshold, "px") && element.style.bottom !== positioning.bottom) {
      // emit event only if position has change
      onReposition && onReposition(refreshToken);
      // provide an extra delay render to ensure the position of the elements
      // are captured correctly by dependent element (i.e. adorner for the dropdown)
      onReposition && delayExecution(onReposition);
    }
    if (positioning.top !== null) {
      element.style.top = positioning.top;
    }
    if (positioning.left !== null) {
      element.style.left = positioning.left;
    }
    if (positioning.bottom !== null) {
      if (disableVerticalAdjustment && positioning.bottom === "".concat(marginThreshold, "px") && heightThreshold) {
        // circuit breaker for paneSize resize event when setting height
        ignoreResizeEventRef.current = true;
        element.style.height = "".concat(heightThreshold - parseInt(positioning.top) - marginThreshold, "px");
        // reset paneSize resize event listener
        delayExecution(function () {
          return ignoreResizeEventRef.current = false;
        });
      } else {
        element.style.bottom = positioning.bottom;
      }
    } else {
      element.style.height = '';
    }
    element.style.transformOrigin = positioning.transformOrigin;
  }, [delayExecution, disableVerticalAdjustment, getPositioningStyle, marginThreshold, onReposition]);
  var onEnterProp = TransitionProps.onEnter,
    onEnteringProp = TransitionProps.onEntering,
    onEnteredProp = TransitionProps.onEntered,
    onExitProp = TransitionProps.onExit,
    onExitedProp = TransitionProps.onExited,
    onExitingProp = TransitionProps.onExiting,
    restTransitionProps = _objectWithoutProperties(TransitionProps, _excluded2);
  var handleEnter = useCallback(function (element, isAppearing) {
    if (onEnter) {
      onEnter(element, isAppearing);
    }
    if (onEnterProp) {
      onEnterProp(element, isAppearing);
    }
  }, [onEnter, onEnterProp]);
  var handleEntering = useCallback(function (element, isAppearing) {
    if (onEntering) {
      onEntering(element, isAppearing);
    }
    if (onEnteringProp) {
      onEnteringProp(element, isAppearing);
    }
    setPositioningStyles();
  }, [onEnteringProp, onEntering, setPositioningStyles]);
  var handleEntered = useCallback(function (element, isAppearing) {
    if (onEntered) {
      onEntered(element, isAppearing);
    }
    if (onEnteredProp) {
      onEnteredProp(element, isAppearing);
    }
    setPositioningStyles();
  }, [onEntered, onEnteredProp, setPositioningStyles]);
  var handleExit = useCallback(function (element) {
    if (onExit) {
      onExit(element);
    }
    if (onExitProp) {
      onExitProp(element);
    }
  }, [onExit, onExitProp]);
  var handleExited = useCallback(function (element) {
    if (onExited) {
      onExited(element);
    }
    if (onExitedProp) {
      onExitedProp(element);
    }
  }, [onExited, onExitedProp]);
  var handleExiting = useCallback(function (element) {
    if (onExiting) {
      onExiting(element);
    }
    if (onExitingProp) {
      onExitingProp(element);
    }
  }, [onExiting, onExitingProp]);
  var handlePaperRef = useCallback(function (instance) {
    paneRef.current = instance;
  }, []);

  // Recalculate popover position when it need to adjust it's position in reference to the container viewport
  useEffect(function () {
    if (!ignoreResizeEventRef.current && open && paneSize) {
      setPositioningStyles(refreshToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paneSize, setPositioningStyles, refreshToken]);
  useImperativeHandle(action, function () {
    return open ? {
      updatePosition: setPositioningStyles
    } : null;
  }, [open, setPositioningStyles]);
  useEffect(function () {
    if (!open) {
      return undefined;
    }
    var handleResize = debounce(setPositioningStyles);
    window.addEventListener('resize', handleResize);
    return function () {
      handleResize.clear();
      window.removeEventListener('resize', handleResize);
    };
  }, [open, setPositioningStyles]);
  var transitionDuration = transitionDurationProp;
  if (transitionDurationProp === 'auto' && !TransitionComponent.supportAuto) {
    transitionDuration = undefined;
  }

  // If the container prop is provided, use that
  // If the anchorEl prop is provided, use its parent body element as the container
  // If neither are provided let the Modal take care of choosing the container
  var container = containerProp || (anchorEl ? ownerDocument(getAnchorEl(anchorEl)).body : undefined);
  var _paneProps$elevation = paneProps.elevation,
    elevation = _paneProps$elevation === void 0 ? 200 : _paneProps$elevation,
    border = paneProps.border,
    __css = paneProps.__css,
    rest = _objectWithoutProperties(paneProps, _excluded3);
  var pane__css = useMemo(function () {
    return _objectSpread2({
      position: 'absolute',
      overflowY: 'auto',
      overflowX: 'hidden',
      maxWidth: 'calc(100% - 2rem)',
      maxHeight: 'calc(100% - 2rem)',
      // save guard when modal is use with disableEnforceFocus set to true
      pointerEvents: 'auto',
      // We disable the focus ring for mouse, touch and keyboard users.
      outline: 0
    }, __css);
  }, [__css]);

  // eslint-disable-next-line no-console
  // console.log(`------ popover render open: ${open} ------`)
  var content = /*#__PURE__*/React.createElement(PanePrivate, _extends({
    "data-csg-test": "Popover",
    "automation-id": "Popover",
    elevation: elevation,
    ref: handlePaperRef,
    styledBorder: border,
    __css: pane__css
  }, rest), typeof children === 'function' ? children() : children);
  return /*#__PURE__*/React.createElement(Modal, _extends({
    container: container,
    open: open,
    ref: ref,
    BackdropProps: backdropProps
  }, other), /*#__PURE__*/React.createElement(TransitionComponent, _extends({
    appear: true,
    "in": open,
    onEnter: handleEnter,
    onEntered: handleEntered,
    onEntering: handleEntering,
    onExit: handleExit,
    onExited: handleExited,
    onExiting: handleExiting,
    timeout: transitionDuration,
    transitionStyles: transitionStyles
  }, restTransitionProps), isNotEmptyObject(clickAwayListenerProps) ? /*#__PURE__*/React.createElement(ClickAwayListener, clickAwayListenerProps, content) : content));
});
if (__DEV__) {
  Popover.displayName = 'Popover';
}

export default Popover;
export { getOffsetLeft, getOffsetTop, getPopoverAndAnchorStatus };
//# sourceMappingURL=popover.esm.js.map
